import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Package, Users, Pencil, Save, X, Plus, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Crown, Shield, Zap } from 'lucide-react';

import { getBackendUrl } from '../../services/origin';
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/publicApi';
const API = getBackendUrl();
const getToken = () => localStorage.getItem('admin_token');
const headers = () => ({ 'Authorization': `Bearer ${getToken()}` });
// Same shim as SuperadminBugReports — uses axios under the hood for
// resilience to the Chrome "body stream already read" race.
const api = async (path, opts = {}) => {
  const method = (opts.method || 'GET').toUpperCase();
  const body = opts.body ? (typeof opts.body === 'string' ? JSON.parse(opts.body) : opts.body) : undefined;
  const url = `/api${path}`;
  let res;
  if (method === 'POST') res = await apiPost(url, body, { headers: headers() });
  else if (method === 'PUT') res = await apiPut(url, body, { headers: headers() });
  else if (method === 'DELETE') res = await apiDelete(url, { headers: headers() });
  else res = await apiGet(url, { headers: headers() });
  return res.data;
};

const FEATURE_LABELS = {
  dashboard: 'Dashboard', analytics: 'Analytics', loyalty: 'Loyalty / Referral',
  engagement: 'Engagement', comunicazione: 'Comunicazione base', comunicazione_pro: 'Comunicazione PRO',
  shop: 'Shop / Ecommerce', settings: 'Impostazioni',
};

const LIMIT_LABELS = {
  max_customers: 'Clienti attivi', max_messages_month: 'Messaggi/mese',
  max_automations: 'Automazioni', max_rewards: 'Premi',
  max_venues: 'Punti vendita', max_staff: 'Staff',
};

const PLAN_ICONS = { starter: Zap, professional: Crown, enterprise: Shield };

function PackageCard({ pkg, onEdit }) {
  const Icon = PLAN_ICONS[pkg.slug] || Package;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`pkg-${pkg.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D44E20]/10 flex items-center justify-center"><Icon size={20} className="text-[#D44E20]" /></div>
          <div>
            <h3 className="font-bold text-[#1A1A1A]">{pkg.name}</h3>
            <p className="text-xs text-slate-400">{pkg.slug}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-[#D44E20]">{pkg.price_monthly}</span>
          <span className="text-xs text-slate-400">EUR/mese</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">{pkg.description}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {Object.entries(pkg.features || {}).map(([k, v]) => (
          <span key={k} className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${v ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
            {FEATURE_LABELS[k] || k}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {Object.entries(pkg.limits || {}).map(([k, v]) => (
          <span key={k} className="text-[10px] text-slate-500">
            {LIMIT_LABELS[k] || k}: <strong>{v === -1 ? 'Illimitati' : v}</strong>
          </span>
        ))}
      </div>
      <button onClick={() => onEdit(pkg)} className="text-xs text-[#D44E20] font-semibold hover:underline flex items-center gap-1">
        <Pencil size={11} /> Modifica
      </button>
    </div>
  );
}

function PackageForm({ pkg, onSave, onCancel }) {
  const [form, setForm] = useState(pkg || {
    name: '', slug: '', price_monthly: 0, description: '',
    features: { dashboard: true, analytics: false, loyalty: false, engagement: false, comunicazione: false, comunicazione_pro: false, shop: false, settings: true },
    limits: { max_customers: 500, max_messages_month: 1000, max_automations: 2, max_rewards: 5, max_venues: 1, max_staff: 3 },
    add_ons_available: [], sort_order: 0, is_active: true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFeature = (k, v) => setForm(f => ({ ...f, features: { ...f.features, [k]: v } }));
  const setLimit = (k, v) => setForm(f => ({ ...f, limits: { ...f.limits, [k]: v } }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-[#1A1A1A]">{pkg?.id ? 'Modifica pacchetto' : 'Nuovo pacchetto'}</h3>
        <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] text-slate-500 font-semibold block mb-1">Nome</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" data-testid="pkg-name-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 font-semibold block mb-1">Slug</label>
          <input value={form.slug} onChange={e => set('slug', e.target.value)} className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 font-semibold block mb-1">Prezzo EUR/mese</label>
          <input type="number" value={form.price_monthly} onChange={e => set('price_monthly', parseFloat(e.target.value) || 0)} className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-slate-500 font-semibold block mb-1">Descrizione</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
      </div>
      <div>
        <label className="text-[10px] text-slate-500 font-semibold block mb-2">Funzionalita attive</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.keys(FEATURE_LABELS).map(k => (
            <button key={k} onClick={() => setFeature(k, !form.features[k])} data-testid={`feature-${k}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                form.features[k] ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
              {form.features[k] ? <ToggleRight size={14} className="text-green-500" /> : <ToggleLeft size={14} />}
              {FEATURE_LABELS[k]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-slate-500 font-semibold block mb-2">Limiti (-1 = illimitato)</label>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {Object.keys(LIMIT_LABELS).map(k => (
            <div key={k}>
              <label className="text-[9px] text-slate-400 block mb-0.5">{LIMIT_LABELS[k]}</label>
              <input type="number" value={form.limits[k] ?? 0} onChange={e => setLimit(k, parseInt(e.target.value))}
                className="w-full h-8 border border-slate-200 rounded-lg px-2 text-xs text-center" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(form)} data-testid="save-pkg-btn"
          className="flex items-center gap-2 px-5 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A]">
          <Save size={14} /> Salva
        </button>
        <button onClick={onCancel} className="px-5 py-2 text-slate-500 text-sm hover:text-slate-700">Annulla</button>
      </div>
    </div>
  );
}

function BrandSubscriptionRow({ sub, packages, onAssign }) {
  const [open, setOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(sub.package_id || '');
  const [overrides, setOverrides] = useState(sub.feature_overrides || {});
  const [limitOverrides, setLimitOverrides] = useState(sub.limit_overrides || {});
  const [saving, setSaving] = useState(false);

  const currentPkg = packages.find(p => p.id === sub.package_id);

  const save = async () => {
    setSaving(true);
    try {
      await api(`/superadmin/brands/${sub.brand_id}/subscription`, {
        method: 'PUT', body: JSON.stringify({ package_id: selectedPkg, feature_overrides: overrides, limit_overrides: limitOverrides })
      });
      toast.success(`Sottoscrizione aggiornata per ${sub.brand_name}`);
      onAssign();
    } catch { toast.error('Errore'); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`sub-${sub.brand_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between text-left">
        <div className="flex items-center gap-3">
          <Users size={16} className="text-slate-400" />
          <div>
            <span className="font-semibold text-sm text-[#1A1A1A]">{sub.brand_name}</span>
            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#D44E20]/10 text-[#D44E20] font-semibold">{sub.package_name}</span>
          </div>
        </div>
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-3">
          <div>
            <label className="text-[10px] text-slate-500 font-semibold block mb-1">Pacchetto</label>
            <select value={selectedPkg} onChange={e => setSelectedPkg(e.target.value)} className="h-9 border border-slate-200 rounded-lg px-3 text-sm">
              <option value="">Nessuno (test mode)</option>
              {packages.map(p => <option key={p.id} value={p.id}>{p.name} — {p.price_monthly} EUR/mese</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-semibold block mb-1">Override funzionalita (sovrascrive il pacchetto)</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(FEATURE_LABELS).map(k => {
                const pkgVal = currentPkg?.features?.[k];
                const overrideVal = overrides[k];
                const isOverridden = overrideVal !== undefined;
                const isEnabled = isOverridden ? overrideVal : pkgVal;
                return (
                  <button key={k} onClick={() => {
                    if (isOverridden) { const copy = {...overrides}; delete copy[k]; setOverrides(copy); }
                    else { setOverrides(o => ({...o, [k]: !pkgVal})); }
                  }} className={`text-[9px] px-2 py-1 rounded-full font-semibold border transition-all ${
                    isOverridden ? (isEnabled ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-red-50 border-red-300 text-red-500')
                    : (isEnabled ? 'bg-green-50 border-green-200 text-green-600' : 'bg-slate-50 border-slate-200 text-slate-400')
                  }`}>
                    {isOverridden && '* '}{FEATURE_LABELS[k]}
                  </button>
                );
              })}
            </div>
            <p className="text-[9px] text-slate-400 mt-1">* = override attivo (click per rimuovere)</p>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-semibold block mb-1">Override limiti (vuoto = usa default pacchetto)</label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {Object.keys(LIMIT_LABELS).map(k => (
                <div key={k}>
                  <label className="text-[9px] text-slate-400 block mb-0.5">{LIMIT_LABELS[k]}</label>
                  <input type="number" value={limitOverrides[k] ?? ''} placeholder={currentPkg?.limits?.[k] === -1 ? '∞' : currentPkg?.limits?.[k]}
                    onChange={e => {
                      if (e.target.value === '') { const c = {...limitOverrides}; delete c[k]; setLimitOverrides(c); }
                      else setLimitOverrides(o => ({...o, [k]: parseInt(e.target.value)}));
                    }}
                    className="w-full h-7 border border-slate-200 rounded-lg px-2 text-[10px] text-center" />
                </div>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-xs font-semibold rounded-lg hover:bg-[#B8411A] disabled:opacity-50">
            <Save size={12} /> {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SuperadminPackages() {
  const [packages, setPackages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState('packages');
  const [editPkg, setEditPkg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [pkgRes, subRes] = await Promise.all([api('/superadmin/packages'), api('/superadmin/subscriptions')]);
    setPackages(pkgRes.packages || []);
    setSubs(subRes.subscriptions || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePkg = async (form) => {
    if (form.id) {
      await api(`/superadmin/packages/${form.id}`, { method: 'PUT', body: JSON.stringify(form) });
      toast.success('Pacchetto aggiornato');
    } else {
      await api('/superadmin/packages', { method: 'POST', body: JSON.stringify(form) });
      toast.success('Pacchetto creato');
    }
    setEditPkg(null); setShowNew(false); load();
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="superadmin-packages">
      <h1 className="text-3xl font-bold tracking-tight text-[#1A1A1A] mb-1">Pacchetti & Sottoscrizioni</h1>
      <p className="text-slate-500 text-sm mb-6">Gestisci i piani, le funzionalita e i limiti per ogni brand</p>

      <div className="flex gap-2 mb-6">
        {[{ key: 'packages', label: 'Pacchetti' }, { key: 'subscriptions', label: 'Brand & Sottoscrizioni' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.key ? 'bg-[#D44E20] text-white' : 'bg-slate-100 text-slate-500'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'packages' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setShowNew(true); setEditPkg(null); }} data-testid="add-pkg-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:bg-[#B8411A]">
              <Plus size={14} /> Nuovo pacchetto
            </button>
          </div>
          {showNew && <PackageForm onSave={savePkg} onCancel={() => setShowNew(false)} />}
          {editPkg && <PackageForm pkg={editPkg} onSave={savePkg} onCancel={() => setEditPkg(null)} />}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map(p => <PackageCard key={p.id} pkg={p} onEdit={setEditPkg} />)}
          </div>
        </div>
      )}

      {tab === 'subscriptions' && (
        <div className="space-y-3">
          {subs.length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <Users size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun brand con sottoscrizione</p>
              <p className="text-xs text-slate-400 mt-1">Assegna un pacchetto ai brand dalla lista sotto</p>
            </div>
          )}
          {subs.map(s => <BrandSubscriptionRow key={s.brand_id} sub={s} packages={packages} onAssign={load} />)}
        </div>
      )}
    </div>
  );
}
